import { Skeleton, Button, Space } from "antd";
import { TableOutlined } from "@ant-design/icons";
import { useCallback, useState } from "react";
import { useEffect } from "react";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import "./ag-grid-theme.css"; // importing the Data Grid theme
import TooltipContainer from "../components/UI/TooltipContainer";
import NoRowsOverlay from "../components/UI/NoRowsOverlay";
import AutoSizer from "react-virtualized-auto-sizer";
import { useGetCurrencySymbolQuery } from "../redux/api/genericReporting";
import { useSelector } from "react-redux";
import { theme } from "antd";
import TableDownloadData from "./TableDownloadData";

export default function TableContainer({
  useApi,
  apiParams,
  title,
  height = null,
  width = "auto",
  tooltipTitle = "WIP",
  includeTooltip = true,
  includeDownloadButton = true,
  minTableWidth = 600,
}) {
  const { token } = theme.useToken();
  const selectedClient = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['client_name']
  );

  const currencySymbolApi = useGetCurrencySymbolQuery({
    clientName: selectedClient,
  });

  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState(null);

  const [rowCount, setRowCount] = useState(100);

  const [gridApi, setGridApi] = useState(null);
  const [tableHeight, setTableHeight] = useState(null);
  const [tableWidth, setTableWidth] = useState(null);

  const root = getComputedStyle(document.documentElement);

  const redColor = root.getPropertyValue("--red-color").trim();
  const greenColor = root.getPropertyValue("--green-color").trim();

  useEffect(() => {
    if (rowData) {
      setRowCount(rowData.length);
    }
  }, [rowData]);

  const api = useApi(apiParams);

  const percentageFormatter = (params) => {
    const value = params.value;
    return value !== null && value !== undefined
      ? `${value.toFixed(2)}%`
      : "0%";
  };

  const numberFormatter = (params) => {
    const value = params.value;
    return value !== null && value !== undefined ? value.toLocaleString() : "0";
  };

  const currencyFormatter = useCallback(
    (params) => {
      const value = params.value;
      if (value === null || value === undefined) {
        return "0";
      }
      const symbol = (currencySymbolApi && currencySymbolApi.data) ? currencySymbolApi.data : "";
      const formattedValue = typeof value === "number" ? value.toLocaleString() : value;
      return `${symbol}${formattedValue}`;
    },
    [currencySymbolApi.data]
  );

  const changeCellStyler = (params) => {
    // if param value is negative, return red color
    if (params.value < 0) {
      return { color: redColor };
    }
    // if param value is positive, return green color
    if (params.value > 0) {
      return { color: greenColor };
    }
  };

  const invertedChangeCellStyler = (params) => {
    // if param value is negative, return green color
    if (params.value < 0) {
      return { color: greenColor };
    }
    // if param value is positive, return red color
    if (params.value > 0) {
      return { color: redColor };
    }
  };

  const formatterMap = {
    percentageFormatter: percentageFormatter,
    numberFormatter: numberFormatter,
    currencyFormatter: currencyFormatter,
  };

  const stylerMap = {
    changeCellStyle: changeCellStyler,
    invertedChangeCellStyle: invertedChangeCellStyler,
  };

  useEffect(() => {
    if (api.status === "fulfilled" && api.data.rowData.length > 0) {
      var tmpColumnDefs = api.data.columnDefs;

      if (tmpColumnDefs && tmpColumnDefs.length > 0) {
        tmpColumnDefs = tmpColumnDefs.map((col) => ({
          ...col,
          valueFormatter:
            formatterMap[col.valueFormatter] != null
              ? formatterMap[col.valueFormatter]
              : null,
          cellStyle:
            stylerMap[col.cellStyle] != null ? stylerMap[col.cellStyle] : null,
        }));
      }

      if (api.data.pinnedBottomRowData && api.data.pinnedBottomRowData.length > 0) {
        setPinnedBottomRowData(api.data.pinnedBottomRowData);
      }
      else {
        setPinnedBottomRowData(null);
      }

      setShowTable(true);
      setIsLoading(false);
      setColumnDefs(tmpColumnDefs);
      setRowData(api.data.rowData);
    } else if (api.status === "rejected") {
      setShowTable(false);
      setIsLoading(false);
      setRowData([]);
      setColumnDefs([]);
      setPinnedBottomRowData(null);
    } else if (api.status === "pending") {
      setShowTable(true);
      setIsLoading(true);
      setRowData([]);
      setColumnDefs([]);
      setPinnedBottomRowData(null);
    } else {
      setShowTable(true);
      setIsLoading(false);
      setRowData([]);
      setColumnDefs([]);
      setPinnedBottomRowData(null);
    }
  }, [api]);

  useEffect(() => {
    if (gridApi) {
      // number that is bigger amongst the two
      const max = Math.max(minTableWidth, tableWidth * 0.92);
      // gridApi.sizeColumnsToFit();
      gridApi.sizeColumnsToFit(max);
    }
  }, [gridApi, rowData, columnDefs, tableHeight, tableWidth, minTableWidth, currencySymbolApi.data]);

  return (
    <>
      {" "}
      {showTable ? (
        <>
          {isLoading ? (
            <div
              style={{
                height: height != null ? height : `100%`,
                width: width,
                maxHeight: "120vh",
                animation: "fadeIn",
              }}
            >
              <AutoSizer
                onResize={({ height, width }) => {
                  setTableHeight(height);
                  setTableWidth(width);
                }}
              >
                {({ height, width }) => (
                  <Skeleton.Node
                    active
                    style={{
                      width: width,
                      height: height,
                      borderRadius: token.cardRadius,
                    }}
                  >
                    <TableOutlined
                      style={{ fontSize: "3em", color: "var(--green-color)" }}
                    />
                  </Skeleton.Node>
                )}
              </AutoSizer>
            </div>
          ) : (
            <div
              style={{
                height: height != null ? height : `${rowCount * 2.15 + 4}rem`,
                width: width,
                maxHeight: "120vh",
                animation: "fadeIn 1.5s",
              }}
            >
              {title ? <h3>{title}</h3> : <></>}
              <AutoSizer
                onResize={({ height, width }) => {
                  setTableHeight(height);
                  setTableWidth(width);
                }}
              >
                {({ height, width }) => (
                  <div
                    style={{
                      width: width,
                      height: height,
                      padding: "24px",
                      "--ag-header-height": "84px",
                      "--ag-row-height": "60px",
                    }}
                  >
                    {includeTooltip && (
                      <TooltipContainer
                        title={tooltipTitle}
                        style={{
                          // align to right
                          display: "flex",
                          justifyContent: "flex-end",
                          marginBottom: "0.5vh",
                          marginRight: "0.5vw",
                        }}
                      />
                    )}
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        // overflow: "auto",
                      }}
                    >
                      <AgGridReact
                        columnDefs={columnDefs} // the columns you want to display
                        rowData={rowData} // the data you want to display
                        pinnedBottomRowData={pinnedBottomRowData}
                        defaultColDef={{
                          wrapHeaderText: true,
                          autoHeaderHeight: true,
                          wrapText: true,
                        }}
                        rowHeight={60}
                        headerHeight={84}
                        groupHeaderHeight={84}
                        onGridReady={(params) => {
                          setGridApi(params.api);
                        }}
                        enableCellTextSelection={true}
                        multiSortKey="ctrl"
                        animateRows={true}
                        suppressCellSelection={false}
                        enableCharts={true}
                        noRowsOverlayComponent={NoRowsOverlay}
                      />
                      <TableDownloadData columnDefs={columnDefs} rowData={rowData} pinnedBottomRowData={pinnedBottomRowData} />
                    </div>
                  </div>
                )}
              </AutoSizer>
              {includeDownloadButton && (
                <>
                  <Space />
                  <Button
                    onClick={() => {
                      if (gridApi) {
                        gridApi.exportDataAsCsv();
                      }
                    }}
                  >
                    Download as CSV
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div
              style={{
                height: height != null ? height : `100%`,
                width: width,
                maxHeight: "120vh",
                animation: "fadeIn",
              }}
            >
              <AutoSizer
                onResize={({ height, width }) => {
                  setTableHeight(height);
                  setTableWidth(width);
                }}
              >
                {({ height, width }) => (
                  <div style={{ width: width, height: height, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <h2 style={{ color: "red" }}>Error</h2>
                  </div>
                )}
              </AutoSizer>
            </div>
      )}
    </>
  );
}
